import { DecisionContext, defaultFinancialYear } from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { DecisionType } from "@/common/decisions";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  YearFieldModel,
} from "@/common/commonForms";

export type ApproveAuditorRemuneration = {
  type: DecisionType.approveAuditorRemuneration;
  isCoveredByInvoice?: boolean;
  specifiedValue?: number;
  /** Year in ISO8601 format */
  period?: string;
  isDeviatingPeriod?: boolean;
  /** LocalDate in ISO8601 format (yyyy-MM-dd) */
  deviatingPeriodFrom?: string;
  /** LocalDate in ISO8601 format (yyyy-MM-dd) */
  deviatingPeriodTo?: string;
};

export class ApproveAuditorRemunerationForm {
  readonly type = DecisionType.approveAuditorRemuneration;
  isCoveredByInvoice: BooleanFieldModel;
  specifiedValue: NumberFieldModel;
  period: YearFieldModel;
  isDeviatingPeriod: BooleanFieldModel;
  deviatingPeriodFrom: LocalDateFieldModel;
  deviatingPeriodTo: LocalDateFieldModel;

  constructor(
    private context: DecisionContext<ApproveAuditorRemuneration>,
    data?: ApproveAuditorRemuneration,
  ) {
    this.isCoveredByInvoice = new BooleanFieldModel({
      value: data?.isCoveredByInvoice,
      required: true,
    });
    this.specifiedValue = new NumberFieldModel({
      value: data?.specifiedValue,
      isPositiveOrZero: true,
    });
    this.period = new YearFieldModel({
      value: data?.period ?? defaultFinancialYear(),
    });
    this.isDeviatingPeriod = new BooleanFieldModel({
      value: data?.isDeviatingPeriod,
      required: true,
    });
    this.deviatingPeriodFrom = new LocalDateFieldModel({ value: data?.deviatingPeriodFrom });
    this.deviatingPeriodTo = new LocalDateFieldModel({ value: data?.deviatingPeriodTo });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  validate(): boolean {
    return (
      this.isCoveredByInvoice.validate() &&
      this.specifiedValue.validate() &&
      this.period.validate() &&
      this.isDeviatingPeriod.validate() &&
      this.deviatingPeriodFrom.validate() &&
      this.deviatingPeriodTo.validate()
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.isCoveredByInvoice.isValid &&
      this.specifiedValue.isValid &&
      this.period.isValid &&
      this.isDeviatingPeriod.isValid &&
      this.deviatingPeriodFrom.isValid &&
      this.deviatingPeriodTo.isValid
    );
  }

  get showSpecifiedValueInput(): boolean {
    return this.isCoveredByInvoice.value === false;
  }

  get showDeviatingPeriodInput(): boolean {
    return this.isDeviatingPeriod.value === true;
  }

  get showRegularPeriodInput(): boolean {
    return this.isDeviatingPeriod.value === false;
  }
}
